import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ActionIcon, Box, Group, Table, Text as MantineText } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Container,
  EmptyMessage,
  LayoutStandard,
  Loader,
  Modal,
  YearPicker,
} from "components";
import { AuthorityConstants } from "constant";
import { HolidayService } from "services";
import { tableClasses } from "styles";
import { HolidayResponse } from "types";
import { AppUtils, DateUtils } from "utils";

import HolidayFormModal from "./HolidayFormModal";
import classes from "./HolidaysPage.module.css";

const sortHolidays = (items: HolidayResponse[]) =>
  [...items].sort((left, right) => {
    if (left.date !== right.date) {
      return left.date < right.date ? 1 : -1;
    }

    return left.name.localeCompare(right.name);
  });

const parseLocalDate = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const HolidaysPage = () => {
  const { t } = useTranslation();
  const [holidays, setHolidays] = useState<HolidayResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [formOpened, setFormOpened] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<HolidayResponse | undefined>(undefined);
  const [holidayToDelete, setHolidayToDelete] = useState<HolidayResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedYear, setSelectedYear] = useState<Date | null>(new Date());
  const tRef = useRef(t);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadHolidays = useCallback(async () => {
    setIsLoading(true);

    try {
      const year = selectedYear?.getFullYear();
      const data = await HolidayService.holidayList(
        year
          ? {
            dateFrom: `${year}-01-01`,
            dateTo: `${year}-12-31`,
          }
          : undefined,
      );
      setHolidays(sortHolidays(data));
    } catch {
      notifications.show({
        color: "red",
        message: tRef.current("holidays.notifications.loadErrorMessage"),
        title: tRef.current("holidays.notifications.loadErrorTitle"),
      });
    } finally {
      setIsLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    void loadHolidays();
  }, [loadHolidays]);

  const closeFormModal = useCallback(() => {
    setFormOpened(false);
    setEditingHoliday(undefined);
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingHoliday(undefined);
    setFormOpened(true);
  }, []);

  const openEditModal = useCallback((holiday: HolidayResponse) => {
    setEditingHoliday(holiday);
    setFormOpened(true);
  }, []);

  const handleHolidaySaved = useCallback(async (holiday: HolidayResponse) => {
    const savedYear = parseLocalDate(holiday.date).getFullYear();

    if (selectedYear && selectedYear.getFullYear() !== savedYear) {
      setSelectedYear(new Date(savedYear, 0, 1));
      return;
    }

    await loadHolidays();
  }, [loadHolidays, selectedYear]);

  const getWeekdayLabel = useCallback(
    (dateString: string) => t(`format.day.${parseLocalDate(dateString).getDay()}`),
    [t],
  );

  const emptyMessage = useMemo(() => {
    if (!selectedYear) {
      return t("holidays.empty");
    }

    return t("holidays.emptyForYear", {
      year: selectedYear.getFullYear(),
    });
  }, [selectedYear, t]);

  const rows = useMemo(() => {
    return holidays.map((holiday) => (
      <Table.Tr key={holiday.id}>
        <Table.Td
          className={classes.nameCell}
          data-label={t("holidays.table.name")}
        >
          <MantineText className={classes.nameValue}>
            {holiday.name}
          </MantineText>
        </Table.Td>
        <Table.Td data-label={t("holidays.table.date")}>
          <span className={classes.dateValue}>
            {DateUtils.formatDateDisplay(holiday.date)}
          </span>
        </Table.Td>
        <Table.Td data-label={t("holidays.table.day")}>
          <span className={classes.dayValue}>
            {getWeekdayLabel(holiday.date)}
          </span>
        </Table.Td>
        <Table.Td
          className={classes.actionsCell}
          data-label={t("actions")}
        >
          <Group gap="xs" className={classes.actionGroup}>
            <Button
              className={classes.actionButton}
              px={0}
              size="xs"
              variant="subtle"
              onClick={() => openEditModal(holiday)}
            >
              {t("holidays.actions.edit")}
            </Button>
            <Button
              className={classes.actionButton}
              color="red"
              px={0}
              size="xs"
              variant="subtle"
              onClick={() => setHolidayToDelete(holiday)}
            >
              {t("action.delete")}
            </Button>
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [getWeekdayLabel, holidays, openEditModal, t]);

  const handleDelete = useCallback(async () => {
    if (!holidayToDelete) {
      return;
    }

    setIsDeleting(true);

    try {
      await HolidayService.holidayDelete(holidayToDelete.id);
      notifications.show({
        color: "green",
        message: tRef.current("holidays.notifications.deleteSuccessMessage"),
        title: tRef.current("holidays.notifications.deleteSuccessTitle"),
      });
      setHolidayToDelete(null);
      await loadHolidays();
    } catch {
      notifications.show({
        color: "red",
        message: tRef.current("holidays.notifications.deleteErrorMessage"),
        title: tRef.current("holidays.notifications.deleteErrorTitle"),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [holidayToDelete, loadHolidays]);

  return (
    <LayoutStandard
      title={AppUtils.composeTabTitle(t("holidays.title"))}
      authority={AuthorityConstants.ROLE_HOL_CUD}
    >
      <Container>
        <div className={classes.page}>
          <div className={classes.content}>
            <Box pos="relative">
              <Loader overlay={true} visible={isLoading} />

              <div className={classes.header}>
                <div className={classes.headerContent}>
                  <h2 className={classes.title}>{t("holidays.title")}</h2>
                  <div className={classes.description}>
                    {t("holidays.description")}
                  </div>
                </div>

                <Group className={classes.headerActions} gap="sm">
                  <Box className={classes.yearFilter}>
                    <YearPicker
                      clearable={true}
                      label={t("holidays.filters.year")}
                      placeholder={t("holidays.filters.yearPlaceholder")}
                      size="sm"
                      value={selectedYear}
                      onChange={(value: Date | null) => setSelectedYear(value)}
                    />
                  </Box>
                  <ActionIcon
                    aria-label={t("holidays.actions.create")}
                    color="green"
                    onClick={openCreateModal}
                    size="input-sm"
                    title={t("holidays.actions.create")}
                  >
                    <IconPlus size={18} />
                  </ActionIcon>
                </Group>
              </div>

              {holidays.length === 0 && !isLoading ? (
                <EmptyMessage message={emptyMessage} />
              ) : (
                <Table className={`${tableClasses.table} ${classes.table}`}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("holidays.table.name")}</Table.Th>
                      <Table.Th>{t("holidays.table.date")}</Table.Th>
                      <Table.Th>{t("holidays.table.day")}</Table.Th>
                      <Table.Th>{t("actions")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>{rows}</Table.Tbody>
                </Table>
              )}
            </Box>
          </div>
        </div>

        <HolidayFormModal
          holiday={editingHoliday}
          onClose={closeFormModal}
          onSaved={handleHolidaySaved}
          opened={formOpened}
        />

        <Modal
          opened={Boolean(holidayToDelete)}
          onClose={() => setHolidayToDelete(null)}
          title={t("holidays.deleteConfirm.title")}
        >
          <MantineText>{t("holidays.deleteConfirm.description")}</MantineText>
          <Group className={classes.modalActions} justify="center">
            <Button color="gray" variant="default" onClick={() => setHolidayToDelete(null)}>
              {t("action.cancel")}
            </Button>
            <Button color="red" disabled={isDeleting} loading={isDeleting} onClick={handleDelete}>
              {t("action.delete")}
            </Button>
          </Group>
        </Modal>
      </Container>
    </LayoutStandard>
  );
};

export default HolidaysPage;
